// components/RoomSettings.jsx: M3-11 host-only settings panel +
// read-only mirror for non-hosts. Sends UPDATE_SETTINGS via the WS
// hook on any change. M3-13's real WS broadcasts SETTINGS_UPDATED;
// the M3-10 stub applies the patch locally so the M3-11 controls
// exercise the right component contracts today.

import { useCallback } from 'preact/hooks';
import '../styles/room-settings.css';

const TARGET_SCORES = /** @type {const} */ ([7, 10]);
const HAND_TIMERS = /** @type {const} */ ([
  { value: null, label: 'None' },
  { value: 20,   label: '20 seconds' },
  { value: 30,   label: '30 seconds' },
  { value: 60,   label: '60 seconds' },
]);

function describeHandTimer(value) {
  const match = HAND_TIMERS.find((t) => t.value === value);
  return match ? match.label : String(value);
}

/**
 * @param {{
 *   isHost: boolean,
 *   settings: {target_score:7|10, hand_timer_seconds:20|30|60|null, stick_the_dealer:boolean, bot_fill:boolean},
 *   onUpdate: (patch: Partial<{target_score:7|10, hand_timer_seconds:20|30|60|null, stick_the_dealer:boolean, bot_fill:boolean}>) => void,
 *   hostName?: string,
 * }} props
 */
export function RoomSettings({ isHost, settings, onUpdate, hostName }) {
  const handleTargetChange = useCallback((e) => {
    const v = Number(/** @type {HTMLInputElement} */ (e.target).value);
    if (v === 7 || v === 10) onUpdate({ target_score: v });
  }, [onUpdate]);

  const handleTimerChange = useCallback((e) => {
    const raw = /** @type {HTMLSelectElement} */ (e.target).value;
    const v = raw === 'null' ? null : Number(raw);
    if (v === null || v === 20 || v === 30 || v === 60) {
      onUpdate({ hand_timer_seconds: v });
    }
  }, [onUpdate]);

  const handleStdToggle = useCallback((e) => {
    onUpdate({ stick_the_dealer: /** @type {HTMLInputElement} */ (e.target).checked });
  }, [onUpdate]);

  const handleBotFillToggle = useCallback((e) => {
    onUpdate({ bot_fill: /** @type {HTMLInputElement} */ (e.target).checked });
  }, [onUpdate]);

  if (!isHost) {
    return (
      <section class="room-settings readonly" data-testid="room-settings" data-mode="readonly" aria-labelledby="room-settings-heading">
        <h2 id="room-settings-heading" class="room-settings-heading">Settings</h2>
        <p class="room-settings-note">Only the host can change these.</p>
        <dl class="room-settings-readonly-list">
          <div class="room-settings-row">
            <dt>Target score</dt>
            <dd data-testid="room-settings-target-score-readonly">{settings.target_score}</dd>
          </div>
          <div class="room-settings-row">
            <dt>Hand timer</dt>
            <dd data-testid="room-settings-hand-timer-readonly">{describeHandTimer(settings.hand_timer_seconds)}</dd>
          </div>
          <div class="room-settings-row">
            <dt>Stick the dealer</dt>
            <dd data-testid="room-settings-std-readonly">{settings.stick_the_dealer ? 'On' : 'Off'}</dd>
          </div>
          <div class="room-settings-row">
            <dt>Bot fill</dt>
            <dd data-testid="room-settings-bot-fill-readonly">{settings.bot_fill ? 'On' : 'Off'}</dd>
          </div>
        </dl>
        <p class="room-settings-waiting" data-testid="room-settings-waiting">
          Waiting for {hostName || 'the host'} to start.
        </p>
      </section>
    );
  }

  return (
    <section class="room-settings host" data-testid="room-settings" data-mode="host" aria-labelledby="room-settings-heading">
      <h2 id="room-settings-heading" class="room-settings-heading">Settings (host)</h2>

      <fieldset class="room-settings-group">
        <legend class="room-settings-label">Target score</legend>
        <div class="room-settings-radio-row" role="radiogroup" aria-label="Target score">
          {TARGET_SCORES.map((score) => (
            <label key={score} class="room-settings-radio">
              <input
                type="radio"
                name="room-settings-target-score"
                value={score}
                data-testid={`room-settings-target-score-${score}`}
                checked={settings.target_score === score}
                onChange={handleTargetChange}
              />
              <span>{score}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div class="room-settings-group">
        <label class="room-settings-label" for="room-settings-hand-timer">Hand timer</label>
        <select
          id="room-settings-hand-timer"
          class="room-settings-select"
          data-testid="room-settings-hand-timer"
          value={settings.hand_timer_seconds === null ? 'null' : String(settings.hand_timer_seconds)}
          onChange={handleTimerChange}
        >
          {HAND_TIMERS.map((opt) => (
            <option key={String(opt.value)} value={opt.value === null ? 'null' : String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div class="room-settings-group">
        <label class="room-settings-toggle">
          <input
            type="checkbox"
            data-testid="room-settings-std"
            checked={settings.stick_the_dealer}
            onChange={handleStdToggle}
          />
          <span>Stick the dealer</span>
        </label>
      </div>

      <div class="room-settings-group">
        <label class="room-settings-toggle">
          <input
            type="checkbox"
            data-testid="room-settings-bot-fill"
            checked={settings.bot_fill}
            onChange={handleBotFillToggle}
          />
          <span>Fill empty seats with bots</span>
        </label>
      </div>
    </section>
  );
}
